import * as React from 'react'
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Slide,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material'
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import {
  ArrowAll,
  Circle,
  Close,
  ContentSave,
  Delete,
  Magnify,
  Menu,
  PencilBox,
  Refresh,
  SetCenter
} from 'mdi-material-ui'
import { TransitionProps } from '@mui/material/transitions'
import FilterVariants from '.'
import { useMutation, useQuery } from 'react-query'
import {
  CurrentFilter,
  deleteFilter,
  editCurrentFilter,
  fetchCurrentFilter,
  fetchFilter,
  Filters,
  FilterType,
  ObjectType,
  saveCurrentFilter,
  saveFilter
} from '@/@core/services/app.service'
import { formatDate, isWindow, unique } from '@/@core/utils/utils'

import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import PositionedSnackbar from '../message'
import { useRouter } from 'next/router'

interface NObject {
  [key: string]: any
}

const color = 'rgb(0, 0, 0,0.6)'
const bgColor = 'white'

const defaultValues = { name: '' }
const schema = yup.object().shape({
  name: yup.string().required()
})

function sleep(duration: number): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve()
    }, duration)
  })
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    maxWidth: 'none'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const AutocompleteColor = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: `${color} !important`
  },
  '& .MuiInputBase-root': {
    borderRadius: 0,
    color: color
  }
}))

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />
})

const FilterList = React.forwardRef(
  (
    props: {
      fn: any
      filterType: {
        title?: any
        key?: string | undefined
        checked?: boolean | undefined
      }
      handleChip1Click?: any
      close?: any
      analysisInfo?: any
    },
    ref
  ) => {
    const { t } = useTranslation()
    const router = useRouter()

    const { id } = router.query
    const type = router.asPath.includes('/samples') ? 'sample' : 'analysis'

    const [config, setConfig] = React.useState<Filters | null>()
    const [name, setName] = React.useState<string>(defaultValues.name)
    const [createConfig, setCreateConfig] = React.useState()
    const [currentConfig, setCurrentConfig] = React.useState()
    const [open, setOpen] = React.useState(false)
    const [selectValue, setSelectValue] = React.useState(null)
    const [options, setOptions] = React.useState<readonly Filters[]>([])
    const loading = open && options.length === 0
    const [createOpen, setCreateOpen] = React.useState(false)
    const [currentFilter, setCurrentFilter] = React.useState<CurrentFilter>()
    const filterRef = React.useRef<any>()
    const filterRef2 = React.useRef<any>()
    const messageRef = React.useRef<any>()
    const [searchValue, setSearch] = React.useState<string>('')

    // ** Hooks
    const {
      control: Control,
      handleSubmit: handleSubmit,
      formState: { errors: Errors }
    } = useForm({
      defaultValues: defaultValues,
      resolver: yupResolver(schema)
    })

    const search = () => {
      props.fn && props.fn(filterRef.current.config)

      // 保存
      const user = JSON.parse(localStorage.getItem('user') as string)
      let currentFilterType = FilterType.SNV
      if (props.filterType && props.filterType.title === 'CNV')
        currentFilterType = FilterType.CNV

      //更新
      if (currentFilter)
        editCurrentFilters.mutateAsync({
          id: currentFilter.id,
          type: currentFilterType,
          filter: filterRef.current.config
        } as CurrentFilter)
      else {
        //保存
        // saveCurrentFilters.mutateAsync({
        //   filter: filterRef.current.config,
        //   createdAt: formatDate(new Date()) as unknown as Date,
        //   type: currentFilterType,
        //   userId: user.id,
        //   objectId: id as unknown as number,
        //   objectType:
        //     type === 'sample' ? ObjectType.SAMPLE : ObjectType.ANALYSIS
        // })
      }
    }

    const save = () => {
      setCreateOpen(true)

      setTimeout(() => {
        filterRef.current.config &&
          filterRef2.current.setConfig &&
          filterRef2.current.setConfig(filterRef.current.config)
      }, 0)
    }

    const reset = () => {
      filterRef.current.reset()

      setConfig(null)

      setSearch('')

      props.fn && props.fn(null)
    }

    React.useEffect(() => {
      let active = true

      if (!loading) {
        return undefined
      }

      // eslint-disable-next-line padding-line-between-statements
      ;(async () => {
        await sleep(1e3) // For demo purposes.

        if (active) {
          setOptions([...(filters?.list as Filters[])])
        }
      })()

      return () => {
        active = false
      }
    }, [loading])

    const saveFilters = useMutation({
      mutationFn: async (data: Filters) => await saveFilter(data),
      onSuccess: (data: Filters) => {
        setCreateOpen(!createOpen)

        messageRef?.current?.handleClick(t('New successful'), 'success')
      },
      onError: (error) => {
        messageRef?.current?.handleClick(t('New failed') + error, 'error')
      }
    })

    const saveCurrentFilters = useMutation({
      mutationFn: async (data: CurrentFilter) => await saveCurrentFilter(data),
      onSuccess: (data: CurrentFilter) => {
        // setCreateOpen(!createOpen)
      },
      onError: (error) => {
        messageRef?.current?.handleClick(t('Saved failed') + error, 'error')
      }
    })

    const editCurrentFilters = useMutation({
      mutationFn: async (data: CurrentFilter) => await editCurrentFilter(data),
      onSuccess: (data: CurrentFilter) => {
        // setCreateOpen(!createOpen)
      },
      onError: (error) => {
        messageRef?.current?.handleClick(t('Saved failed') + error, 'error')
      }
    })

    const fetchCurrentFilters = useMutation({
      mutationFn: async (data: CurrentFilter) => await fetchCurrentFilter(data),
      onSuccess: (data: { list: CurrentFilter[]; count: number }) => {
        //

        if (data && data.list && data.list.length) {
          const filter = data.list[0]

          //当前历史过滤器
          setCurrentFilter(filter)

          if (
            filter.objectType &&
            filter.objectType === ObjectType.SAMPLE &&
            filter.type === FilterType.CNV
          ) {
            props.handleChip1Click('2')
          } else if (
            filter.objectType &&
            filter.objectType === ObjectType.SAMPLE
          ) {
            props.handleChip1Click('1')
          }

          filterRef.current.setConfig(filter.filter)

          props.fn && props.fn(filter.filter)
        }
      },
      onError: (error) => {
        messageRef?.current?.handleClick(t('Saved failed') + error, 'error')
      }
    })

    const deleteFilters = useMutation({
      mutationFn: async (id: number) => await deleteFilter(id),
      onSuccess: (data: Filters) => {
        // setCreateOpen(!createOpen)
        setOpen(true)

        setOptions([])

        refetch()

        messageRef?.current?.handleClick(t('Delete successfully'), 'success')
      },
      onError: (error) => {
        messageRef?.current?.handleClick(t('Delete failed') + error, 'error')
      }
    })

    const { data: filters, refetch } = useQuery<{
      list: Filters[]
    }>(
      ['filters', searchValue],
      async () => {
        const query = {
          pageNum: 1,
          pageSize: 1000
        } as {
          pageNum: number
          pageSize: number
          searchValue?: string
        }
        if (searchValue) query.searchValue = searchValue

        return await fetchFilter(query)
      },
      {
        keepPreviousData: true
      }
    )

    // //获取历史config过滤器
    // const once = React.useMemo(() => {
    //   const user = JSON.parse(localStorage.getItem('user') as string)

    //   fetchCurrentFilters.mutateAsync({
    //     userId: user.id,
    //     objectId: id as unknown as number,
    //     objectType: type === 'sample' ? ObjectType.SAMPLE : ObjectType.ANALYSIS
    //   } as CurrentFilter)
    // }, [])

    React.useEffect(() => {
      if (!open) {
        setOptions([])
      }
    }, [open])

    React.useImperativeHandle(ref, () => {
      return {
        reset
      }
    })

    return (
      <Box
        display={'flex'}
        flexDirection="column"
        bgcolor={bgColor}
        flex={1}
        maxWidth={'20rem'}
        maxHeight="73vh"
        height={'100%'}
        justifyContent="space-between"
      >
        <Box
          bgcolor={'#FAFAFA'}
          px={4}
          pt={2}
          height={50}
          position={'sticky'}
          top={0}
          zIndex={10}
          borderBottom={'1px solid'}
          boxShadow={'0 0 15px 0 #ccc'}
        >
          <Stack
            direction={'row'}
            alignItems="center"
            justifyContent={'space-between'}
          >
            <Typography fontSize={18} fontWeight={600} component={'h3'}>
              {t('Filters')}
            </Typography>

            <IconButton
              onClick={() => {
                //

                props.close && props.close(false)
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Stack flex={1} paddingTop={2} paddingLeft={2} paddingRight={2}>
          <Grid container alignItems={'center'} spacing={2}>
            <Grid item xs={8}>
              <FormControl fullWidth size="small">
                <AutocompleteColor
                  sx={{
                    '& .MuiButtonBase-root': {
                      color: color
                    }
                  }}
                  size="small"
                  id="asynchronous-demo"
                  open={open}
                  onOpen={() => {
                    setOpen(true)

                    refetch()
                  }}
                  onClose={() => {
                    setOpen(false)
                  }}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.name === value.name
                  }
                  onChange={(
                    e: React.SyntheticEvent<Element, Event>,
                    v: any | null
                  ) => {
                    // 加载过滤器
                    //
                    if (v) setConfig(v)
                    if (v && v.filter) filterRef.current.setConfig(v?.filter)
                  }}
                  popupIcon={<ExpandCircleDownOutlinedIcon />}
                  getOptionLabel={(option: any) => option.name as string}
                  options={unique((filters?.list ?? []) as Filters[], 'name')}
                  loading={loading}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}

                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                      placeholder={t('Select a filter set')}
                      onChange={(e) => {
                        setSearch(e.target.value)
                      }}
                      sx={{ color: color, bgcolor: bgColor }}
                    />
                  )}
                  renderOption={(props, option: any, { selected }) => {
                    return (
                      <li
                        {...props}
                        style={{
                          justifyContent: 'space-between'
                        }}
                      >
                        {option.name}

                        <IconButton
                          onClick={(e) => {
                            deleteFilters.mutateAsync(option.id)

                            if (selected) {
                              setConfig(null)
                            }

                            e.stopPropagation()
                          }}
                          title={t('delete filters')}
                        >
                          <Delete />
                        </IconButton>
                      </li>
                    )
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={() => setCreateOpen(true)}
                fullWidth
              >
                {t('create')}
              </Button>
            </Grid>
          </Grid>

          <Box width={'20rem'} overflow="auto">
            <FilterVariants
              color={color}
              fn={setCurrentConfig}
              ref={filterRef}
              analysisInfo={props.analysisInfo}
              filterType={props.filterType}
            />

            {/* <List>
            {configList.map((items) => {
              return (
                <React.Fragment key={items.title}>
                  <ListItem>
                    <Grid
                      container
                      alignItems={'center'}
                      justifyContent={'space-between'}
                    >
                      <Grid item>
                        <Stack direction={'row'} spacing={2}>
                          <ArrowAll />

                          <Typography>{items.title}</Typography>
                        </Stack>
                      </Grid>

                      <Grid item>
                        <Stack direction="row" alignItems={'center'}>
                          <IconButton
                            onClick={() => {
                              setFilterConfigs(
                                filterConfigs.map((item) => {
                                  if (item.title === items.title) {
                                    item.checked = !item.checked
                                  }

                                  return item
                                })
                              )
                            }}
                          >
                            <Circle
                              color={items.checked ? 'success' : 'error'}
                              sx={{
                                fontSize: '0.9375rem',
                                cursor: 'point'
                              }}
                            />
                          </IconButton>

                          <IconButton>
                            <PencilBox />
                          </IconButton>

                          <IconButton>
                            <Delete />
                          </IconButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </ListItem>

                  <Box paddingLeft={8}>
                    <List component="div" disablePadding>
                      <ListItem sx={{ flexDirection: 'column' }}>
                        <Grid
                          container
                          alignItems={'center'}
                          justifyContent={'space-between'}
                        >
                          <Grid item>
                            <Stack direction={'row'} spacing={2}>
                              <Menu />

                              <Typography>FilterConfig_1</Typography>
                            </Stack>
                          </Grid>

                          <Grid item>
                            <Stack direction="row" alignItems={'center'}>
                              <IconButton>
                                <Circle
                                  color="success"
                                  sx={{
                                    fontSize: '0.9375rem',
                                    cursor: 'point'
                                  }}
                                />
                              </IconButton>

                              <IconButton>
                                <PencilBox />
                              </IconButton>
                            </Stack>
                          </Grid>
                        </Grid>

                        <Box>
                          <Typography textAlign={'center'}>
                            Frequency: 0.0-0.01
                          </Typography>
                        </Box>
                      </ListItem>
                    </List>
                  </Box>
                </React.Fragment>
              )
            })}
          </List> */}
          </Box>
        </Stack>

        <Box
          bgcolor={'#FAFAFA'}
          px={4}
          py={2}
          position={'sticky'}
          bottom={0}
          zIndex={10}
          borderTop={'1px solid'}
          boxShadow={'15px 0 0 0 #ccc'}
        >
          <Stack direction={'row'} spacing={4} justifyContent={'end'}>
            <Box>
              <Button
                onClick={() => {
                  filterRef.current.reset()

                  setConfig(null)

                  setSearch('')

                  props.fn && props.fn(null)
                }}
                // eslint-disable-next-line lines-around-comment
                // startIcon={<Refresh />}
                variant={'outlined'}
                size={'small'}
              >
                {t('Reset Filters')}
              </Button>
            </Box>

            <Box>
              <Button
                variant={'contained'}
                size={'small'}
                onClick={save}

                // startIcon={<ContentSave />}
              >
                {t('save')}
              </Button>
            </Box>

            <Box>
              <Button
                variant={'contained'}
                size={'small'}
                onClick={search}

                // startIcon={<Magnify />}
              >
                {t('Apply Filters')}
              </Button>
            </Box>
          </Stack>
        </Box>

        {/* create */}

        <BootstrapDialog
          onClose={() => setCreateOpen(!createOpen)}
          aria-labelledby="customized-dialog-title"
          open={createOpen}
          TransitionComponent={Transition}
        >
          <form
            onSubmit={handleSubmit((data) => {
              //   setCreateOpen(!createOpen)
              // 保存
              const user = JSON.parse(localStorage.getItem('user') as string)

              saveFilters.mutateAsync({
                name: data.name,
                filter: filterRef2.current.config,
                createdAt: formatDate(new Date()) as unknown as Date,
                type: FilterType.SNV,
                user_id: user.id
              })
            })}
          >
            <DialogTitle
              sx={{ m: 0, p: 2, bgcolor: 'rgb(227, 226, 228)' }}
              id="customized-dialog-title"
            >
              {`(${t('Filter Details')})`}
            </DialogTitle>

            <IconButton
              aria-label="close"
              onClick={() => setCreateOpen(!createOpen)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogContent
              dividers
              sx={{
                minWidth: 800,
                maxWidth: 800,
                bgcolor: 'rgb(227, 226, 228)'
              }}
            >
              <Box>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <Controller
                    name="name"
                    control={Control}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                      <TextField
                        value={value}
                        onChange={(e) => {
                          onChange(e)
                        }}
                        placeholder={"Set Filter's Name"}
                        error={Boolean(Errors.name)}
                        size="small"
                      />
                    )}
                  />

                  {Errors.name && (
                    <FormHelperText sx={{ color: 'error.main' }}>
                      {Errors.name.message}
                    </FormHelperText>
                  )}
                </FormControl>

                <Box maxHeight={'60vh'}>
                  <FilterVariants
                    color="black"
                    bgColor="rgb(227, 226, 228)"
                    fn={setCreateConfig}
                    ref={filterRef2}
                    analysisInfo={props.analysisInfo}
                    filterType={props.filterType}
                  />
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ bgcolor: 'rgb(227, 226, 228)' }}>
              <Stack direction="row" spacing={4} padding={'10px 0'}>
                <Button
                  variant="outlined"
                  size="small"
                  autoFocus
                  onClick={() => setCreateOpen(!createOpen)}
                >
                  {t('close')}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  autoFocus
                  type="submit"
                >
                  {t('save')}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  autoFocus
                  onClick={() => {
                    filterRef2.current.reset()
                  }}
                >
                  {t('reset')}
                </Button>
              </Stack>
            </DialogActions>
          </form>
        </BootstrapDialog>

        <PositionedSnackbar ref={messageRef} />
      </Box>
    )
  }
)

export default FilterList
